import React, { createContext, useContext, useState, ReactNode } from "react";
import { translations, Language } from "../translations";

/**
 * Language Context Interface
 * 
 * Defines the shape of the language context that will be available to all components.
 * - language: Current selected language ('ne' for Nepali or 'en' for English)
 * - setLanguage: Function to change the current language
 * - t: Translation function that takes a key and returns the translated string
 */
interface LanguageContextType {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

// Create the context with undefined as initial value
// This will be populated by the LanguageProvider
const LanguageContext = createContext<LanguageContextType | undefined>(undefined);

/**
 * Language Provider Component
 * 
 * This component wraps the entire application and provides language functionality
 * to all child components. It manages the current language state and provides
 * a translation function.
 * 
 * Usage: Wrap your app with <LanguageProvider> in App.tsx
 */
export function LanguageProvider({ children }: { children: ReactNode }) {
  // State to track current language, defaulting to English ('en')
  const [language, setLanguage] = useState<Language>("en");

  /**
   * Translation function
   * 
   * Takes a dot-notation key (e.g., "auth.login") and returns the translated string
   * based on the current language. If the key is not found, it returns the key itself.
   * 
   * @param key - Dot-notation key to lookup translation (e.g., "common.appName")
   * @returns Translated string in current language
   * 
   * Example: t("auth.login") returns "लग इन" in Nepali or "Log In" in English
   */
  const t = (key: string): string => {
    // Split the key by dots to navigate nested translation objects
    const keys = key.split(".");
    let value: any = translations[language];
    
    // Navigate through the nested structure
    for (const k of keys) {
      value = value?.[k];
    }
    
    // Return the found value or the original key if not found
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

/**
 * Custom Hook: useLanguage
 * 
 * A custom hook to access the language context in any component.
 * Must be used within a component that is wrapped by LanguageProvider.
 * 
 * @returns LanguageContextType object with language, setLanguage, and t function
 * @throws Error if used outside of LanguageProvider
 * 
 * Usage:
 * const { language, setLanguage, t } = useLanguage();
 * const welcomeText = t("auth.welcome");
 */
export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error("useLanguage must be used within LanguageProvider");
  }
  return context;
}